// src/components/features/invitations/AcceptInvitationPage.js
//
// The page a workspace-member or team-agent invitation email links to
// (route: /accept-invite). Rebuilt on the same branded shell as WelcomePage
// (src/components/features/invitations/WelcomePage.js) — see that file's
// header for why these public invitation routes never import the
// authenticated app shell, the workspace store, or any account-bootstrap
// hook.
//
// Differs from WelcomePage in one deliberate way: this invitation is scoped
// to an EXISTING workspace an inviter already controls, so the caller's
// email is checked against the invitation record server-side
// (getWorkspaceInvitationPreview). Signed out, the preview still renders —
// content must be visible before sign-in resolves, same as WelcomePage — but
// signed in with the wrong Google account, the callable reports status
// 'email_mismatch' and this page offers a REAL way out: a button that calls
// signOut(auth) directly, not prose telling the invitee to do it themselves.
//
// The preview is fetched from the token query parameter on mount without
// waiting for auth to resolve, same pattern as WelcomePage. authUser is
// tracked only to decide which action the primary button performs.
//
// On a successful acceptance this page navigates to "/" and deliberately
// skips the post-acceptance tour launch WelcomePage performs — that handoff
// is reserved for a brand-new customer claiming an admin-created account,
// not for someone joining a workspace that already exists.
//
// Decline uses the canonical Modal primitive (src/components/ui/primitives/
// Modal.js) rather than a native browser confirm dialog: Modal has no
// isOpen prop, so mounting IS open — {declineOpen && <Modal …>} — and
// maxWidth is passed inline, never as a dynamic arbitrary Tailwind class
// (see Modal.js's own header comment for the JIT trap that would otherwise
// cause).

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../../../firebase';
import * as invitationService from '../../../services/invitationService';
import InvitationShell from './InvitationShell';
import InvitationStatePanel from './InvitationStatePanel';
import Modal from '../../ui/primitives/Modal';
import Button from '../../ui/primitives/Button';
import { usePendingAction } from '../../../hooks/usePendingAction';

import { SUPPORT_EMAIL } from '../../../config/support';
import { clearStash } from '../../../utils/unsavedWork';

function useQueryToken() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search).get('token') || '', [search]);
}

/**
 * @param {{ initialPreview?: object|null, initialAuthUser?: object|null, initialLoadError?: string }} props
 *   The three `initial*` props seed this component's state and exist purely
 *   for deterministic render-smoke testing — see WelcomePage's identical
 *   props for the full rationale. In production no caller passes them, so
 *   the page always starts from the real fetch and the real (initially
 *   unresolved) auth state.
 */
export default function AcceptInvitationPage({ initialPreview = null, initialAuthUser = null, initialLoadError = '' } = {}) {
    const token = useQueryToken();
    const navigate = useNavigate();
    const [authUser, setAuthUser] = React.useState(initialAuthUser);
    const [preview, setPreview] = React.useState(initialPreview);
    const [loadError, setLoadError] = React.useState(initialLoadError);
    const [actionError, setActionError] = React.useState('');
    const [declineOpen, setDeclineOpen] = React.useState(false);

    // Tracked for the button label only — never gates the initial render.
    React.useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, setAuthUser);
        return unsubscribe;
    }, []);

    // Fetches on mount from the token, independent of auth state, so the
    // invitation's content renders before sign-in resolves.
    React.useEffect(() => {
        if (!token) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await invitationService.getInvitationPreview(token);
                if (!cancelled) setPreview(data);
            } catch (err) {
                if (!cancelled) setLoadError(err?.message || 'Could not load this invitation.');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const signIn = async () => {
        setActionError('');
        try {
            await signInWithPopup(auth, new GoogleAuthProvider());
        } catch (err) {
            setActionError(err?.message || 'Sign-in failed.');
        }
    };
    const { run: handleSignIn, pending: signingIn } = usePendingAction(signIn);

    const handleSignOut = async () => {
        setActionError('');
        try {
            clearStash();
            await signOut(auth);
        } catch (err) {
            setActionError(err?.message || 'Sign-out failed.');
        }
    };

    const accept = async () => {
        setActionError('');
        try {
            await invitationService.acceptInvitation(token);
            navigate('/');
        } catch (err) {
            setActionError(err?.message || 'Failed to accept invitation.');
        }
    };
    const { run: handleAccept, pending: accepting } = usePendingAction(accept);

    const decline = async () => {
        setActionError('');
        try {
            await invitationService.declineInvitation(token);
            setDeclineOpen(false);
            navigate('/');
        } catch (err) {
            setActionError(err?.message || 'Failed to decline invitation.');
            setDeclineOpen(false);
        }
    };
    const { run: handleDecline, pending: declining } = usePendingAction(decline);
    // One shared guard across the two actions: while either is in flight the
    // other control stays disabled, and only the pressed one shows its busy label.
    const submitting = accepting || declining || signingIn;

    if (!token) {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invalid invitation link"
                    body="This link is missing its invitation token. Check the link from your email, or ask whoever invited you to resend it."
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (loadError) {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Could not load this invitation"
                    body={`${loadError} If this keeps happening, reach us at ${SUPPORT_EMAIL}.`}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (!preview) {
        return (
            <InvitationShell>
                <InvitationStatePanel title="Loading…" body="One moment." />
            </InvitationShell>
        );
    }

    if (preview.status === 'email_mismatch') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Wrong account"
                    body={`This invitation is for ${preview.inviteeEmail}. Sign out, then sign back in with that address to view it.`}
                    tone="danger"
                    action={{ label: 'Sign out', onClick: handleSignOut }}
                />
                {actionError && (
                    <p className="text-sm text-[color:var(--color-danger)] mt-4">{actionError}</p>
                )}
            </InvitationShell>
        );
    }

    if (preview.status === 'expired') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation expired"
                    body={`Ask whoever invited you to send a new one, or reach us at ${SUPPORT_EMAIL}.`}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'accepted') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Already accepted"
                    body="This invitation has already been accepted. Sign in to open the workspace."
                    action={{ label: 'Sign in', href: '/login' }}
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'declined') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation declined"
                    body={`This invitation was already declined. Contact whoever invited you if you'd like a new one, or reach us at ${SUPPORT_EMAIL}.`}
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    if (preview.status === 'revoked') {
        return (
            <InvitationShell>
                <InvitationStatePanel
                    title="Invitation revoked"
                    body="This invitation was revoked. Contact whoever invited you if you still need a workspace."
                    tone="danger"
                />
            </InvitationShell>
        );
    }

    // status === 'pending'
    const roleLabel = preview.role === 'team_agent' ? 'Team Agent' : 'Member';

    return (
        <InvitationShell>
            <h1 className="text-lg font-semibold mb-2 text-[color:var(--text-primary)]">You&rsquo;re invited</h1>
            <p className="text-sm text-[color:var(--text-secondary)] mb-2">
                <strong>{preview.inviterDisplayName || 'A teammate'}</strong> invited you to join{' '}
                <strong>{preview.workspaceName || 'a workspace'}</strong> as a {roleLabel}
                {preview.role === 'team_agent' && preview.tawTierName
                    ? <> on the <strong>{preview.tawTierName}</strong> tier</>
                    : null}.
            </p>
            <p className="text-sm text-[color:var(--text-muted)] mb-6">
                Invitation sent to <strong>{preview.inviteeEmail}</strong>.
            </p>
            {actionError && (
                <p className="text-sm text-[color:var(--color-danger)] mb-4">{actionError}</p>
            )}
            <div className="flex gap-2">
                <Button
                    variant="accent"
                    onClick={authUser ? handleAccept : handleSignIn}
                    loading={authUser ? accepting : signingIn}
                    loadingLabel={authUser ? 'Accepting…' : 'Signing in…'}
                    disabled={submitting}
                    className="flex-1"
                >
                    {authUser ? 'Accept' : 'Sign in with Google to accept'}
                </Button>
                <Button
                    variant="ghost"
                    onClick={() => setDeclineOpen(true)}
                    disabled={submitting}
                    className="flex-1"
                >
                    Decline
                </Button>
            </div>
            {declineOpen && (
                <Modal
                    title="Decline this invitation?"
                    onClose={() => setDeclineOpen(false)}
                    maxWidth={420}
                    footer={
                        <>
                            <Button variant="ghost" onClick={() => setDeclineOpen(false)} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button
                                variant="accent"
                                onClick={handleDecline}
                                loading={declining}
                                loadingLabel="Declining…"
                                disabled={submitting}
                            >
                                Decline invitation
                            </Button>
                        </>
                    }
                >
                    <p className="text-sm text-[color:var(--text-secondary)]">
                        You can be re-invited later if you change your mind.
                    </p>
                </Modal>
            )}
        </InvitationShell>
    );
}
